import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Banknote, CreditCard, Wallet, X } from 'lucide-react';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import {
  ADDRESS_LINE2_OPTIONS,
  ADDRESS_LINE2_OTHER,
  PAY_LATER_ERRORS,
  PAY_LATER_SETTLE_METHODS,
  lookupPayLaterCustomer,
  mapPayLaterError,
  newClientPaymentId,
} from '../../api/payLaters';
import { posOfflineQueue } from '../../utils/posOfflineQueue';
import { formatCurrency } from '../../data/mockData';
import { payableForTender, validatePaidNow } from '../../utils/posMoney';
import { roundMoney } from '../../utils/soldBy';
import styles from './PayLaterCustomerModal.module.css';

const EMPTY = {
  fullName: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  addressLine2Other: '',
  block: '',
  flat: '',
  paidNow: '',
  paidNowMethod: 'CASH',
};

const METHOD_ICONS = {
  CASH: Banknote,
  UPI: Wallet,
  CARD: CreditCard,
};

function isPresetLine2(value) {
  return ADDRESS_LINE2_OPTIONS.includes(value) && value !== ADDRESS_LINE2_OTHER;
}

export default function PayLaterCustomerModal({
  amount = 0,
  initialPhone = '',
  token,
  submitting = false,
  offline = false,
  lotNote = false,
  onClose,
  onConfirm,
}) {
  const [form, setForm] = useState({
    ...EMPTY,
    phone: String(initialPhone || '').replace(/\D/g, '').slice(0, 10),
  });
  const [error, setError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [knownBalance, setKnownBalance] = useState(null);
  const [balanceIsLastKnown, setBalanceIsLastKnown] = useState(false);
  const phoneRef = useRef(null);
  const addressListRef = useRef(null);
  const lookupSeq = useRef(0);
  const [addressListMax, setAddressListMax] = useState(null);
  const paymentStamp = useRef({ key: '', id: '', at: '' });

  const line2SelectValue = isPresetLine2(form.addressLine2)
    ? form.addressLine2
    : (form.addressLine2 || form.addressLine2Other ? ADDRESS_LINE2_OTHER : '');
  const isOther = line2SelectValue === ADDRESS_LINE2_OTHER;

  useEffect(() => {
    requestAnimationFrame(() => phoneRef.current?.focus());
  }, []);

  useLayoutEffect(() => {
    const list = addressListRef.current;
    if (!list) {
      setAddressListMax(null);
      return;
    }
    const cards = [...list.querySelectorAll('[data-prior-address]')];
    if (cards.length <= 3) {
      setAddressListMax((prev) => (prev == null ? prev : null));
      return;
    }
    const gap = Number.parseFloat(getComputedStyle(list).rowGap) || 8;
    const height = Math.ceil(
      cards.slice(0, 3).reduce((sum, card) => sum + card.getBoundingClientRect().height, 0) + gap * 2,
    );
    setAddressListMax((prev) => (prev === height ? prev : height));
  }, [addresses]);

  useEffect(() => {
    const digits = form.phone;
    if (digits.length !== 10 || !token) {
      setAddresses([]);
      setOpenCount(0);
      return undefined;
    }
    const seq = ++lookupSeq.current;
    setLookupLoading(true);
    const applyBook = (data, { lastKnown }) => {
      if (seq !== lookupSeq.current || !data) return;
      setAddresses(data.addresses || []);
      setOpenCount(data.openCount || 0);
      setKnownBalance(data.balance == null ? null : (Number(data.balance) || 0));
      setBalanceIsLastKnown(lastKnown);
      if (data.customerName && !form.fullName.trim()) {
        setForm((prev) => (prev.fullName.trim() ? prev : { ...prev, fullName: data.customerName }));
      }
    };
    const timer = setTimeout(() => {
      const load = offline
        ? posOfflineQueue.knownPhone(digits).then((data) => {
          applyBook(data?.hasBook ? data : { addresses: [], openCount: 0, balance: null, customerName: null }, { lastKnown: true });
        })
        : lookupPayLaterCustomer(digits, token).then(async (data) => {
          await posOfflineQueue.rememberPhone({
            phone: digits,
            customerName: data.customerName,
            balance: data.balance,
            openCount: data.openCount,
            addresses: data.addresses,
          });
          const adjusted = await posOfflineQueue.knownPhone(digits);
          const drifted = adjusted.balance !== (Number(data.balance) || 0);
          applyBook({
            ...data,
            balance: adjusted.balance,
            openCount: adjusted.openCount,
          }, { lastKnown: drifted });
        });
      load
        .catch(() => {
          if (seq !== lookupSeq.current) return;
          return posOfflineQueue.knownPhone(digits).then((data) => {
            applyBook(data?.hasBook ? data : null, { lastKnown: true });
          });
        })
        .finally(() => {
          if (seq === lookupSeq.current) setLookupLoading(false);
        });
    }, 280);
    return () => clearTimeout(timer);
    // Only re-lookup when phone / token change — not when name is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.phone, token, offline]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const applyAddress = (addr) => {
    if (!addr) return;
    const line2 = addr.addressLine2 || '';
    setForm((prev) => ({
      ...prev,
      fullName: addr.customerName || prev.fullName,
      addressLine1: addr.addressLine1 || '',
      addressLine2: isPresetLine2(line2) ? line2 : (line2 ? ADDRESS_LINE2_OTHER : ''),
      addressLine2Other: isPresetLine2(line2) ? '' : line2,
      block: addr.block || '',
      flat: addr.flat || '',
    }));
    setError('');
  };

  const resolveAddressLine2 = () => {
    if (isOther) return form.addressLine2Other.trim();
    if (isPresetLine2(form.addressLine2)) return form.addressLine2;
    return '';
  };

  const hasLine1 = Boolean(form.addressLine1.trim());
  const addressLine2 = resolveAddressLine2();
  const hasLine2 = Boolean(addressLine2);
  const blockFlatBlocked = !hasLine2;

  useEffect(() => {
    if (!blockFlatBlocked) return;
    setForm((prev) => {
      if (!prev.block && !prev.flat) return prev;
      return { ...prev, block: '', flat: '' };
    });
  }, [blockFlatBlocked]);

  const paidNowCheck = validatePaidNow(form.paidNow, amount);
  const hasPaidNow = !paidNowCheck.omit && paidNowCheck.ok;
  const paidNowMethod = String(form.paidNowMethod || '').toUpperCase();
  const cashQuote = payableForTender(hasPaidNow ? paidNowCheck.amount : 0, 'CASH');
  const cashPayable = paidNowMethod === 'CASH' ? cashQuote.payable : paidNowCheck.amount;
  const dueAfter = hasPaidNow ? roundMoney(Math.max(0, roundMoney(amount) - paidNowCheck.amount)) : roundMoney(amount);

  const validate = () => {
    if (!form.fullName.trim() || form.fullName.trim().length > 80) {
      return 'Enter the customer’s full name (1–80 characters).';
    }
    if (form.phone.length !== 10) return 'Enter a valid 10-digit phone number.';
    // Line 1 and Line 2 are mutually optional — at least one is required.
    if (!hasLine1 && !hasLine2) {
      if (isOther) return 'Enter the society / locality name, or fill Address Line 1.';
      return 'Enter Address Line 1 or select a society.';
    }
    if (hasLine2) {
      if (!form.block.trim()) return 'Enter the block.';
      if (!form.flat.trim()) return 'Enter the flat.';
    }
    if (!paidNowCheck.ok) return PAY_LATER_ERRORS[paidNowCheck.code];
    if (hasPaidNow) {
      if (!['CASH', 'UPI', 'CARD'].includes(paidNowMethod)) {
        return PAY_LATER_ERRORS.invalid_paid_now_method;
      }
    }
    return '';
  };

  const paymentIdentity = (slice, method) => {
    const key = `${slice}|${method}`;
    if (paymentStamp.current.key !== key) {
      paymentStamp.current = {
        key,
        id: newClientPaymentId(),
        at: new Date().toISOString(),
      };
    }
    return paymentStamp.current;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    const stamp = !offline && hasPaidNow ? paymentIdentity(paidNowCheck.amount, paidNowMethod) : null;
    const includeBlockFlat = hasLine2;
    try {
      await onConfirm({
        fullName: form.fullName.trim().slice(0, 80),
        phone: form.phone,
        addressLine1: form.addressLine1.trim(),
        addressLine2,
        block: includeBlockFlat ? form.block.trim() : '',
        flat: includeBlockFlat ? form.flat.trim() : '',
        paidNow: !offline && hasPaidNow ? paidNowCheck.amount : undefined,
        paidNowMethod: !offline && hasPaidNow ? paidNowMethod : undefined,
        clientPaymentId: stamp?.id,
        settledAt: stamp?.at,
      });
    } catch (err) {
      setError(mapPayLaterError(err) || err?.message || 'Could not save pay later.');
    }
  };

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="pay-later-title">
      <form className={styles.modal} onSubmit={handleSubmit}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close" disabled={submitting}>
          <X size={16} />
        </button>
        <h2 id="pay-later-title">Pay Later — customer details</h2>
        <p className={styles.subtitle}>
          Bill total <strong>{formatCurrency(amount)}</strong>. Name and phone are required.
          Collect part now, or leave the whole bill due.
          {openCount > 0 && (
            <span className={styles.openHint}> · {openCount} open due{openCount === 1 ? '' : 's'} on this phone</span>
          )}
          {knownBalance != null && (
            <span className={styles.openHint}>
              {' · '}
              {balanceIsLastKnown ? 'Last known' : 'Open balance'}
              {' '}
              {formatCurrency(knownBalance)}
            </span>
          )}
        </p>
        {lotNote && (
          <p className={styles.lookupHint}>
            A lot will be picked when this bill syncs. You can still put it on Pay Later.
          </p>
        )}

        <label className={styles.field}>
          <span>Phone number</span>
          <div className={styles.phoneInput}>
            <span>+91</span>
            <input
              ref={phoneRef}
              type="text"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="10-digit mobile"
              value={form.phone}
              maxLength={10}
              onChange={(e) => setField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              disabled={submitting}
              required
            />
          </div>
          {lookupLoading && <span className={styles.lookupHint}>Looking up prior addresses…</span>}
        </label>

        {addresses.length > 0 && (
          <div className={styles.suggestions}>
            <p className={styles.suggestionsLabel}>Prior addresses</p>
            <div
              ref={addressListRef}
              className={`${styles.suggestionList} ${addressListMax != null ? styles.suggestionListScroll : ''}`}
              style={addressListMax != null ? { maxHeight: addressListMax } : undefined}
            >
            {addresses.map((addr, idx) => (
              <button
                key={`${addr.addressLine1}-${addr.flat}-${idx}`}
                type="button"
                data-prior-address=""
                className={styles.suggestionBtn}
                onClick={() => applyAddress(addr)}
                disabled={submitting}
              >
                <strong>{addr.customerName || 'Customer'}</strong>
                {addr.addressLine2 && (
                  <span className={styles.suggestionAddress}>{addr.addressLine2}</span>
                )}
                {(addr.block || addr.flat) && (
                  <span className={styles.suggestionAddress}>
                    {[addr.block && `Blk ${addr.block}`, addr.flat && `Flat ${addr.flat}`].filter(Boolean).join(' · ')}
                  </span>
                )}
                {addr.addressLine1 && (
                  <span className={styles.suggestionAddress}>{addr.addressLine1}</span>
                )}
              </button>
            ))}
            </div>
          </div>
        )}

        <label className={styles.field}>
          <span>Full name</span>
          <input
            type="text"
            value={form.fullName}
            onChange={(e) => setField('fullName', e.target.value.slice(0, 80))}
            autoComplete="name"
            disabled={submitting}
            maxLength={80}
            required
          />
        </label>

        {/* Address order: society dropdown → block/flat → line 1 */}
        <label className={styles.field}>
          <span>Society {hasLine1 ? '(optional)' : ''}</span>
          <select
            value={line2SelectValue}
            onChange={(e) => {
              const next = e.target.value;
              setForm((prev) => ({
                ...prev,
                addressLine2: next === ADDRESS_LINE2_OTHER ? ADDRESS_LINE2_OTHER : next,
                addressLine2Other: next === ADDRESS_LINE2_OTHER ? prev.addressLine2Other : '',
              }));
              setError('');
            }}
            disabled={submitting}
          >
            <option value="">Select society</option>
            {ADDRESS_LINE2_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </label>

        {isOther && (
          <label className={styles.field}>
            <span>Society / locality {hasLine1 ? '(optional)' : ''}</span>
            <input
              type="text"
              value={form.addressLine2Other}
              onChange={(e) => setField('addressLine2Other', e.target.value)}
              placeholder="Enter society or locality"
              disabled={submitting}
            />
          </label>
        )}

        <div className={styles.row}>
          <label className={styles.field}>
            <span>Block {blockFlatBlocked ? '(n/a)' : ''}</span>
            <input
              type="text"
              value={form.block}
              onChange={(e) => setField('block', e.target.value)}
              disabled={submitting || blockFlatBlocked}
              required={!blockFlatBlocked}
            />
          </label>
          <label className={styles.field}>
            <span>Flat {blockFlatBlocked ? '(n/a)' : ''}</span>
            <input
              type="text"
              value={form.flat}
              onChange={(e) => setField('flat', e.target.value)}
              disabled={submitting || blockFlatBlocked}
              required={!blockFlatBlocked}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span>Address line 1 {hasLine2 ? '(optional)' : ''}</span>
          <input
            type="text"
            value={form.addressLine1}
            onChange={(e) => setField('addressLine1', e.target.value)}
            placeholder="Street / landmark"
            disabled={submitting}
          />
        </label>

        {!offline && (
        <div className={styles.paidNow}>
          <p className={styles.suggestionsLabel}>Paid now (optional)</p>
          <p className={styles.lookupHint}>
            Leave blank to put the whole bill on Pay Later. A partial payment must be less than the bill total.
          </p>
          <label className={styles.field}>
            <span>Amount toward this bill</span>
            <div className={styles.moneyInput}>
              <span>₹</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0 = full Pay Later"
                value={form.paidNow}
                onChange={(e) => setField('paidNow', e.target.value)}
                disabled={submitting}
                aria-label="Amount paid now"
              />
            </div>
          </label>
          {String(form.paidNow).trim() !== '' && Number(form.paidNow) !== 0 && (
            <>
              <div className={styles.methodRow} role="group" aria-label="Paid now method">
                {PAY_LATER_SETTLE_METHODS.map(({ key, label }) => {
                  const Icon = METHOD_ICONS[key] || Wallet;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`${styles.methodBtn} ${paidNowMethod === key ? styles.methodActive : ''}`}
                      onClick={() => setField('paidNowMethod', key)}
                      disabled={submitting}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  );
                })}
              </div>
              {hasPaidNow && paidNowMethod === 'CASH' && cashQuote.round_off !== 0 && (
                <p className={styles.lookupHint}>
                  Cash rounds this slice to {formatCurrency(cashPayable)}
                  {' '}
                  ({cashQuote.round_off > 0 ? '+' : ''}
                  {formatCurrency(cashQuote.round_off)}). The bill total stays {formatCurrency(amount)}.
                </p>
              )}
              {hasPaidNow && (
                <p className={styles.duePreview}>
                  Paid now {formatCurrency(paidNowCheck.amount)} via {paidNowMethod}
                  {' · '}
                  due {formatCurrency(dueAfter)}
                </p>
              )}
            </>
          )}
        </div>
        )}

        {error && <AuthMessage>{error}</AuthMessage>}

        <div className={styles.actions}>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting
              ? 'Saving…'
              : hasPaidNow
                ? `Pay ${formatCurrency(paidNowCheck.amount)} now · due ${formatCurrency(dueAfter)}`
                : `Confirm Pay Later · ${formatCurrency(amount)}`}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
